import { useState } from 'react';

export type SortDir = 'asc' | 'desc';

/**
 * Tiny table-sort state hook. Clicking the active column flips direction;
 * clicking a new column sorts it ascending. Pages with server-side paging pass
 * `sortKey`/`sortDir` into their API query; full-array pages feed them to
 * {@link sortRows}.
 */
export function useSort(initialKey: string, initialDir: SortDir = 'asc') {
  const [sortKey, setSortKey] = useState<string>(initialKey);
  const [sortDir, setSortDir] = useState<SortDir>(initialDir);
  const onSort = (k: string) => {
    if (k === sortKey) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(k); setSortDir('asc'); }
  };
  return { sortKey, sortDir, onSort };
}

/** Null/blank-last comparison; numeric-aware, case-insensitive for strings. */
export function compareValues(a: unknown, b: unknown): number {
  const an = a == null || a === '';
  const bn = b == null || b === '';
  if (an && bn) return 0;
  if (an) return 1;
  if (bn) return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
}

/** Returns a new array sorted by `get(row)` in the given direction (blanks last). */
export function sortRows<T>(rows: T[], get: (r: T) => unknown, dir: SortDir): T[] {
  const out = [...rows].sort((x, y) => compareValues(get(x), get(y)));
  return dir === 'asc' ? out : out.reverse();
}

/** Case-insensitive substring match of `term` against the given field values. */
export function matchesQuery(term: string, fields: Array<string | number | null | undefined>): boolean {
  const q = term.trim().toLowerCase();
  if (!q) return true;
  return fields.some((f) => f != null && String(f).toLowerCase().includes(q));
}
